import React, { useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import { Control, Controller } from 'react-hook-form'
import { BenefitProgramFormValues } from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/useBenefitProgramForm'
import { InputContainer } from 'Components/Inputs/ControlledSelect'
import {
  CategoryTaxability,
  CategoryTaxabilitySelector
} from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/CategoryTaxabilitySelector'

type ControlledChipsProps = {
  options: { id: number; name: string; description: string }[]
  control: Control<BenefitProgramFormValues>
  name: string
  defaultSelected?: number[]
  defaultTaxableSettings?: Record<number, CategoryTaxability>
  onTaxableChange?: (categoryId: number, taxable: CategoryTaxability) => void
}

export const CategoryMultiselect = ({
  options,
  control,
  name,
  defaultSelected,
  defaultTaxableSettings = {},
  onTaxableChange
}: ControlledChipsProps) => {
  const [taxableSettings, setTaxableSettings] =
    useState<Record<number, CategoryTaxability>>(defaultTaxableSettings)

  const handleTaxableChange = (categoryId: number, taxable: CategoryTaxability) => {
    setTaxableSettings((prev) => ({ ...prev, [categoryId]: taxable }))
    onTaxableChange?.(categoryId, taxable)
  }

  return (
    <InputContainer>
      <Controller
        name={name as keyof BenefitProgramFormValues}
        control={control}
        defaultValue={defaultSelected}
        render={({ field }) => {
          const selectedCategories = options.filter((option) =>
            (field.value as number[]).includes(option.id)
          )

          return (
            <Box>
              <Autocomplete
                multiple
                id="category-multi-select"
                options={options}
                getOptionLabel={(option) => option.name}
                value={selectedCategories}
                onChange={(_, newValue) => {
                  field.onChange(newValue.map((item) => item.id))
                  newValue.forEach((category) => {
                    if (!(category.id in taxableSettings)) {
                      handleTaxableChange(category.id, true)
                    }
                  })
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label="Program Categories" />
                )}
              />

              {selectedCategories.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <p>Category Taxability:</p>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {selectedCategories.map((category) => (
                      <CategoryTaxabilitySelector
                        key={category.id}
                        label={category.name}
                        value={taxableSettings[category.id] ?? true}
                        onChange={(value) => handleTaxableChange(category.id, value)}
                        name={`taxable-${category.id}`}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )
        }}
      />
    </InputContainer>
  )
}
